/**
 * Pitch Prediction Service
 *
 * Client-side service for fetching pitch prediction data from the backend.
 * Provides pattern recognition, biomechanical signatures, and thumbnail filtering.
 *
 * @module lib/predictionService
 */

// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// ═══════════════════════════════════════════════════════════════════════════
// API CALLS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Fetch prediction overview for a pitcher
 * @param {string} pitcherId - Pitcher identifier
 * @returns {Promise<Object>} Prediction overview data
 */
export async function fetchPitcherPrediction(pitcherId) {
  const response = await fetch(`${API_BASE_URL}/api/pitch-prediction/${pitcherId}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to fetch prediction for ${pitcherId}`);
  }

  return response.json();
}

/**
 * Fetch tell→pitch type patterns for a pitcher
 * @param {string} pitcherId - Pitcher identifier
 * @param {Object} options - Query options
 * @param {string} options.sort - Sort field (exploitability, confidence, occurrences)
 * @param {string} options.order - Sort order (asc, desc)
 * @returns {Promise<Object>} Pattern data
 */
export async function fetchPitcherPatterns(pitcherId, { sort = 'exploitability', order = 'desc' } = {}) {
  const params = new URLSearchParams({ sort, order });
  const response = await fetch(`${API_BASE_URL}/api/pitch-prediction/${pitcherId}/patterns?${params}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to fetch patterns for ${pitcherId}`);
  }

  return response.json();
}

/**
 * Fetch biomechanical signatures for a pitcher
 * @param {string} pitcherId - Pitcher identifier
 * @param {string} pitchType - Optional specific pitch type
 * @returns {Promise<Object>} Biomechanical signature data
 */
export async function fetchBiomechanics(pitcherId, pitchType = null) {
  const params = pitchType ? `?pitch_type=${pitchType}` : '';
  const response = await fetch(`${API_BASE_URL}/api/pitch-prediction/${pitcherId}/biomechanics${params}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to fetch biomechanics for ${pitcherId}`);
  }

  return response.json();
}

/**
 * Fetch video thumbnails with filters
 * @param {string} pitcherId - Pitcher identifier
 * @param {Object} filters - Filter options
 * @param {string[]} filters.pitchTypes - Pitch types to include
 * @param {string[]} filters.confidenceLevels - Confidence levels (CRITICAL, HIGH, MEDIUM, LOW)
 * @param {string[]} filters.tellTypes - Tell types to filter by
 * @param {boolean} filters.tippingOnly - Only show tipping detections
 * @param {number} filters.limit - Max results
 * @param {number} filters.offset - Pagination offset
 * @returns {Promise<Object>} Thumbnail data
 */
export async function fetchThumbnails(pitcherId, filters = {}) {
  const params = new URLSearchParams();

  if (filters.pitchTypes?.length > 0) {
    params.set('pitch_type', filters.pitchTypes.join(','));
  }
  if (filters.confidenceLevels?.length > 0) {
    params.set('confidence_level', filters.confidenceLevels.join(','));
  }
  if (filters.tellTypes?.length > 0) {
    params.set('tell_type', filters.tellTypes.join(','));
  }
  if (filters.tippingOnly) {
    params.set('tipping_only', 'true');
  }
  if (filters.limit) {
    params.set('limit', filters.limit.toString());
  }
  if (filters.offset) {
    params.set('offset', filters.offset.toString());
  }

  const response = await fetch(`${API_BASE_URL}/api/pitch-prediction/${pitcherId}/thumbnails?${params}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to fetch thumbnails for ${pitcherId}`);
  }

  return response.json();
}

// ═══════════════════════════════════════════════════════════════════════════
// CLIENT-SIDE FILTERING
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Filter thumbnails client-side (for bidirectional sync)
 * @param {Array} thumbnails - Array of thumbnail data
 * @param {Object} filters - Active filters
 * @returns {Array} Filtered thumbnails
 */
export function filterThumbnails(thumbnails, filters) {
  if (!thumbnails || !Array.isArray(thumbnails)) return [];

  return thumbnails.filter(thumb => {
    // Pitch type filter
    if (filters.pitchTypes?.length > 0) {
      if (!filters.pitchTypes.includes(thumb.pitchType)) return false;
    }

    // Confidence level filter
    if (filters.confidenceLevels?.length > 0) {
      if (!filters.confidenceLevels.includes(thumb.confidenceLevel)) return false;
    }

    // Tell type filter
    if (filters.tellTypes?.length > 0) {
      const hasTell = thumb.tells?.some(t => filters.tellTypes.includes(t));
      if (!hasTell) return false;
    }

    // Tipping only filter
    if (filters.tippingOnly && !thumb.tippingDetected) return false;

    return true;
  });
}

/**
 * Extract unique filter options from thumbnails
 * @param {Array} thumbnails - Array of thumbnail data
 * @returns {Object} Available filter options with counts
 */
export function extractFilterOptions(thumbnails) {
  if (!thumbnails || !Array.isArray(thumbnails)) {
    return {
      pitchTypes: [],
      confidenceLevels: [],
      tellTypes: []
    };
  }

  const pitchTypeCounts = {};
  const confidenceLevelCounts = {};
  const tellTypeCounts = {};

  for (const thumb of thumbnails) {
    // Count pitch types
    if (thumb.pitchType) {
      pitchTypeCounts[thumb.pitchType] = (pitchTypeCounts[thumb.pitchType] || 0) + 1;
    }

    // Count confidence levels
    if (thumb.confidenceLevel) {
      confidenceLevelCounts[thumb.confidenceLevel] = (confidenceLevelCounts[thumb.confidenceLevel] || 0) + 1;
    }

    // Count tell types
    if (thumb.tells) {
      for (const tell of thumb.tells) {
        tellTypeCounts[tell] = (tellTypeCounts[tell] || 0) + 1;
      }
    }
  }

  return {
    pitchTypes: Object.entries(pitchTypeCounts).map(([type, count]) => ({ type, count })),
    confidenceLevels: Object.entries(confidenceLevelCounts).map(([level, count]) => ({ level, count })),
    tellTypes: Object.entries(tellTypeCounts).map(([type, count]) => ({ type, count }))
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

export const PITCH_TYPE_LABELS = {
  FB: 'Fastball',
  SI: 'Sinker',
  CT: 'Cutter',
  CB: 'Curveball',
  SL: 'Slider',
  CH: 'Changeup',
  SP: 'Splitter',
  KC: 'Knuckle Curve'
};

export const CONFIDENCE_LEVEL_COLORS = {
  CRITICAL: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
  HIGH: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' },
  MEDIUM: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' },
  LOW: { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/30' }
};

export const TELL_CATEGORY_LABELS = {
  glove_position: 'Glove Position',
  timing: 'Timing/Rhythm',
  grip: 'Grip Adjustment',
  arm_slot: 'Arm Slot',
  body_position: 'Body Position',
  head_movement: 'Head Movement'
};

export default {
  fetchPitcherPrediction,
  fetchPitcherPatterns,
  fetchBiomechanics,
  fetchThumbnails,
  filterThumbnails,
  extractFilterOptions,
  PITCH_TYPE_LABELS,
  CONFIDENCE_LEVEL_COLORS,
  TELL_CATEGORY_LABELS
};
